import BaseModel, { executePaste_Parameters } from "./BaseModel";
import BaseObject from "./BaseObject";
import { copyAllProperties, sortArrayByIntProperty } from "./utils";
import Logger from "../Logger";

const logger = new Logger();
//////////////////////////////////////////////////
import ObjectFactory from "./ObjectFactory";
import BaseModelReference from "./BaseModelReference";
import LineTreeViewModel from "../modelTreeViews/LineTreeViewModel";
import Area from "./Area";
import { channelTypeInfo } from "./ChannelType";
import { itx } from "../translationItx/itx";

ObjectFactory.registerClass("Line", (data?: any, parent?: BaseModel) => {
  return new Line({});
});
//////////////////////////////////////////////////
export default class Line extends BaseModel {
  public get className(): string {
    return "Line"; // no itx
  }

  get isPersistent(): boolean {
    return false;
  }

  lineNumber: number = 0;

  override getName(): string | undefined {
    const area = this.parent as Area | undefined;
    return area ? globalThis.state?.settings?.getTopologyLineName(area.areaNumber, this.lineNumber) : undefined;
  }

  override setName(newValue: string): void {
    const area = this.parent as Area | undefined;
    if (area && globalThis.state?.settings) {
      state.settings.setTopologyLineName(area.areaNumber, this.lineNumber, newValue);
    }
    this._name = newValue;
  }

  // -----------------------------------------------------------------------------
  // createdByUser
  // -----------------------------------------------------------------------------
  _createdByUser: boolean = false;
  get createdByUser(): boolean {
    return this._createdByUser;
  }

  set createdByUser(newValue: boolean) {
    if (this._createdByUser === newValue) return;
    this.beforeChange("createdByUser");
    this._createdByUser = newValue;
    this.changed("createdByUser");
  }

  // -----------------------------------------------------------------------------

  getDeviceReferences(): Array<BaseModelReference> {
    const res = new Array<BaseModelReference>();

    const parentArea = this.parent as Area;
    const sLineNumber = this.lineNumber.toString();

    const areaNumber = (parentArea?.areaNumber || 0).toString();
    let devices = state
      .allDevices()
      .filter((device) => device.lineAddress === sLineNumber && device.areaAddress === areaNumber);

    devices = sortArrayByIntProperty(devices, "physicalAddress");

    devices.forEach((device) => {
      if (!channelTypeInfo(device.channelType).isSupply) {
        const ref = new BaseModelReference(
          { overwriteFulltag: `${device.physicalAddress}: ${device.fullTag}`, referencedObject: device },
          this,
        );
        ref.usefromRefencedTreeviewObject.menuButtons = true;
        ref.usefromRefencedTreeviewObject.icon = true;
        res.push(ref);
      }
    });

    return res;
  }

  getPersistentProperties(): string[] {
    return ["itemNumber", "createdByUser", ...super.getPersistentProperties()]; // no itx
  }

  emptyTagIsForbidden(): boolean {
    return state.floors.size > 1;
  }

  canBeEditedInteractive(): boolean {
    return false;
  }

  constructor(data?: any, parent?: BaseObject) {
    super(data, parent);
    copyAllProperties(data, this);
    const area = parent as Area | undefined;
    const storedName = area && globalThis.state?.settings?.getTopologyLineName(area.areaNumber, this.lineNumber);
    if (storedName !== undefined) this._name = storedName;
    this.treeViewModel = new LineTreeViewModel(this);
  }

  // for delete
  canBeDeletedInteractive(): boolean {
    let hasDevices = false;
    state.allDevices().forEach((device) => {
      if (device.lineAddress == `${this.lineNumber}` && device.areaAddress == `${(this.parent as Area)?.areaNumber}`) {
        hasDevices = true;
      }
    });

    return !hasDevices;
  }
  getDeleteConfirmText(): string | undefined {
    let hasDevices = false;
    state.allDevices().forEach((device) => {
      if (device.lineAddress == `${this.lineNumber}` && device.areaAddress == `${(this.parent as Area)?.areaNumber}`) {
        hasDevices = true;
      }
    });
    if (hasDevices) {
      return itx("line.deleteConfirmText", `Linie T1 hat zugewiesene Geräte. Wirklich löschen?`, {
        T1: this.lineNumber,
      });
    }
    return undefined;
  }

  delete(): Array<BaseObject> {
    this.isDeleting = true;

    var res = new Array<BaseObject>();
    const area = this.parent as Area;

    // find new place for the devices that are on the line to delete
    const firstLine = area.lines.asArray().find((line) => line !== this);
    state.allDevices().forEach((device) => {
      if (device.lineAddress == `${this.lineNumber}` && device.areaAddress == `${area?.areaNumber}`) {
        device.lineAddress = `${firstLine?.lineNumber || 0}`;
        device.raiseOnChange("lineAddress");
      }
    });
    if (area) {
      state.settings.setTopologyLineName(area.areaNumber, this.lineNumber, "");
    }
    res = res.concat(super.delete());
    area.updateLines();
    area.raiseOnChange("childCollection");
    return res;
  }

  //////////////////////////////////////////////////////
  ///////////// for copy

  getExtendedCopyCollection(startItem: BaseModel): Array<BaseModel> {
    const res = new Array<BaseModel>();
    return res;
  }
  //////////////////////////////////////////////////////
  ///////////// for paste

  mapAfterCopy() {
    if (!this.mapAfterCopyDone) {
      super.mapAfterCopy();
    }
  }
  async pasteItem(params: { item: BaseModel; allItems: Array<BaseModel> }): Promise<Array<BaseModel>> {
    return [];
  }

  async executePaste(params: executePaste_Parameters): Promise<Array<BaseModel>> {
    const { parent, isCopyRootObj } = params;
    var undoObjects: Array<BaseModel> = [this];

    return undoObjects;
  }

  ////////////////////////////////////////////////////
  //// consistencyCheck
  consistencyCheck(parent: BaseModel) {
    // check first here
    super.consistencyCheck(parent);
    this.getDeviceReferences().forEach((child) => {
      child.consistencyCheck(this);
    });
  }

  setTag(newValue: string) {
    const lineNumber = parseInt(newValue);
    if (lineNumber >= 0 && lineNumber <= 63) {
      const oldLineNumber = this.lineNumber;
      const area = this.parent as Area;
      if (area && oldLineNumber !== lineNumber) {
        state.settings.moveTopologyLineName(area.areaNumber, oldLineNumber, lineNumber);
      }
      this.lineNumber = parseInt(newValue);
      this._tag = `${this.lineNumber}`;

      if (area) {
        state.allDevices().forEach((device) => {
          if (device.lineAddress == `${oldLineNumber}` && device.areaAddress == `${area.areaNumber}`) {
            device.lineAddress = `${this.lineNumber}`;
            device.raiseOnChange("lineAddress");
          }
        });
      }
    }
  }
}
